import { Tooltip } from "@xola/ui-kit";
import { isEquipmentItem, isEveryItemCanceled, now, PurchaseItemStatus } from "common";
import React, { useState } from "react";
import { useMutation } from "react-query";
import {
    CheckInVerificationStatus,
    Event,
    Experience,
    ExperiencePurchaseItem,
    FormGuest,
    Guest,
    GuestCheckInAction,
    GuestStatus,
    Purchase,
} from "types";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { useResource } from "../../../hooks/useResource";
import { useUpdateEffect } from "../../../hooks/useUpdateEffect";
import { useVisibility } from "../../../hooks/useVisibility";
import { updateTimeslotCache } from "../../Event/EditEventCapacityModal";
import { CheckInAndPrintTicket } from "../../Settings/App/PurchaseCreate/CheckInAndPrintTicket";
import { GuestNoShowModal } from "../GuestNoShowModal";
import { CheckInButton } from "./CheckInButton";
import { getCheckInStatusInfo, isItemLevelCheckIn } from "./CheckInHelper";
import {
    useCheckIfMemberVerificationRequired,
    useMemberVerificationModal,
} from "@/components/PurchaseModification/CheckInPurchaseGuests/useMemberVerificationModal";

enum ButtonSource {
    NONE = "none",
    GUEST_CHECK_IN = "guestCheckIn",
    GROUP_CHECK_IN = "guestGroupCheckIn",
    GUEST_NO_SHOW = "guestNoShow",
    GROUP_NO_SHOW = "groupNoShow",
    GROUP_RETURN_EQUIPMENT = "groupReturnEquipment",
    GUEST_RETURN_EQUIPMENT = "guestReturnEquipment",
}

interface SaveGuestStatusPayload {
    guestStatus: GuestStatus;
    guests: Guest[];
    verificationStatus?: CheckInVerificationStatus;
}

interface CheckInPurchaseGuestMenuProps {
    purchase: Purchase;
    purchaseItem: ExperiencePurchaseItem;
    guest?: Guest;
    isOrganizer?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
}

export const CheckInPurchaseGuestMenu = ({
    purchase,
    purchaseItem,
    guest,
    isOrganizer = false,
    onOpenChange,
}: CheckInPurchaseGuestMenuProps) => {
    const invalidate = useInvalidate();
    const seller = useSeller();
    const { isGranted } = useAuthorization();

    const { data: experience } = useResource<Experience>(["products", purchaseItem.product.id]);
    const { data: event } = useResource<Event>(["events", purchaseItem.event.id], null, {
        enabled: !!purchaseItem.event?.id && !isEveryItemCanceled(purchase),
    });
    const { isMemberVerificationRequired } = useCheckIfMemberVerificationRequired(purchaseItem);
    const { verifyMember, MemberVerificationModal } = useMemberVerificationModal();

    const isCheckInEnabled = !!seller?.preferences.checkIn?.enabled || experience?.checkInPreference?.enabled;

    const [selectedGuestsForCheckIn, setSelectedGuestsForCheckIn] = useState<Guest[] | null>(null);
    const [isGuestForCheckInModalOpen, { show: showGuestForCheckInModal, hide: hideGuestForCheckInModal }] =
        useVisibility(false);

    const [isGuestNoShowModalOpen, { show: showGuestNoShowModal, hide: hideGuestNoShowModal }] = useVisibility(false);
    const [noShowGuestDetails, setGuestNoShowDetails] = useState({ count: 1, guestId: "" });

    const totalGuestsCount = purchaseItem.guests.length;
    const {
        areAllGuestsNoShow,
        areAllGuestsCheckedIn,
        isCheckedIn,
        isReturned,
        isNoShow,
        areGuestsCheckedInOrReturnedOnly,
    } = getCheckInStatusInfo(purchaseItem, guest);

    const isEarlyReturnEnabled = experience?.earlyReturn ?? false;
    const showEquipmentReturn = (isReturned || isCheckedIn || areGuestsCheckedInOrReturnedOnly) && isEarlyReturnEnabled;
    // if returned and only early return is enabled, then disable No show and Check-in
    const disableCheckin = isReturned && isEarlyReturnEnabled;

    const { mutateAsync: saveGuestStatus, isLoading: isSaving } = useMutation(
        async ({ guestStatus, guests, verificationStatus }: SaveGuestStatusPayload) => {
            // Equipment without collect-demographics has no guests; apply check-in status at the item level.
            // The backend (ExperiencePurchaseItem::hydrate) sets the item guestStatus directly. XS-1821.
            if (isItemLevelCheckIn(purchaseItem)) {
                await api.patch(`/purchases/${purchase.id}`, {
                    items: [{ id: purchaseItem.id, guestStatus }],
                });
                return;
            }

            await api.patch(`/purchases/${purchase.id}`, {
                items: [
                    {
                        id: purchaseItem.id,
                        guests: guests.map((guest) => {
                            const guestPayload: Partial<FormGuest> = { id: guest.id, guestStatus };

                            const alreadyCheckedInOnce = [GuestStatus.ARRIVED, GuestStatus.RETURNED].includes(
                                guest.guestStatus,
                            );
                            if (guestStatus === GuestStatus.ARRIVED && !alreadyCheckedInOnce) {
                                /** As we only allow single check-in for scheduled experiences (which are displayed in th roster); `incrementCheckInCount` should be only added when guest status is changed from "noshow" or "pending". No need to check in already checked-in guests  */
                                guestPayload.incrementCheckInCount = GuestCheckInAction.CHECK_IN;
                                guestPayload.checkIns = [
                                    {
                                        createdAt: now(),
                                        verificationStatus,
                                    },
                                ];
                            }

                            if (
                                alreadyCheckedInOnce &&
                                (guestStatus === GuestStatus.PENDING || guestStatus === GuestStatus.NO_SHOW)
                            ) {
                                guestPayload.incrementCheckInCount = GuestCheckInAction.UNDO_CHECK_IN;
                                if (guest.checkIns && guest.checkIns.length > 0) {
                                    const lastCheckIn = guest.checkIns.pop()!;
                                    guestPayload.checkIns = [{ ...lastCheckIn, deletedAt: now() }];
                                }
                            }

                            return guestPayload;
                        }),
                    },
                ],
            });
        },
    );

    const { mutateAsync: updateEvent, isLoading: isUpdating } = useMutation(async (payload: Event) => {
        return api.put(`/events/${payload.id}`, payload);
    });

    useUpdateEffect(() => {
        onOpenChange?.(isGuestForCheckInModalOpen || isGuestNoShowModalOpen);
    }, [isGuestForCheckInModalOpen, isGuestNoShowModalOpen]);

    const handleGroupNoShowClick = async (doNotIncreaseAvailability = false) => {
        const guestStatus = areAllGuestsNoShow ? GuestStatus.PENDING : GuestStatus.NO_SHOW;
        const { guests } = purchaseItem;
        handleGuestStatusChange(guestStatus, guests, doNotIncreaseAvailability, ButtonSource.GROUP_NO_SHOW);
    };

    const handleGroupCheckInClick = async () => {
        const guestStatus = isCheckedIn ? GuestStatus.PENDING : GuestStatus.ARRIVED;
        const { guests } = purchaseItem;

        // Item-level check-in (no guests) cannot route through the per-guest ticket-print modal; apply the
        // item guestStatus directly. Ticket printing for such items happens via the post-payment flow. XS-1821.
        if (seller?.preferences?.ticket?.enabled && !areAllGuestsCheckedIn && !isItemLevelCheckIn(purchaseItem)) {
            setSelectedGuestsForCheckIn(guests);
            showGuestForCheckInModal();
            return;
        }

        let verificationStatus;
        if (
            guestStatus === GuestStatus.ARRIVED &&
            getGuestsToBeCheckedIn(guests).length > 0 &&
            isMemberVerificationRequired()
        ) {
            verificationStatus = await verifyMember();
            if (!verificationStatus) return;
        }

        handleGuestStatusChange(guestStatus, guests, false, ButtonSource.GROUP_CHECK_IN);
    };

    const handleGuestCheckInClick = async (id: ID) => {
        const guest = purchaseItem.guests.find((guest) => guest.id === id);
        if (!guest) return;

        const guestStatus = guest?.guestStatus === GuestStatus.ARRIVED ? GuestStatus.PENDING : GuestStatus.ARRIVED;
        if (seller?.preferences?.ticket?.enabled && guest?.guestStatus !== GuestStatus.ARRIVED) {
            setSelectedGuestsForCheckIn([guest]);
            showGuestForCheckInModal();
            return;
        }

        let verificationStatus;
        if (
            guestStatus === GuestStatus.ARRIVED &&
            getGuestsToBeCheckedIn([guest]).length > 0 &&
            isMemberVerificationRequired()
        ) {
            verificationStatus = await verifyMember();
            if (!verificationStatus) return;
        }

        handleGuestStatusChange(guestStatus, [guest], false, ButtonSource.GUEST_CHECK_IN, verificationStatus);
    };

    const handleGroupReturnClick = async () => {
        const guestStatus = isReturned ? GuestStatus.ARRIVED : GuestStatus.RETURNED;
        const { guests } = purchaseItem;
        handleGuestStatusChange(guestStatus, guests, false, ButtonSource.GROUP_RETURN_EQUIPMENT);
    };

    const handleGuestReturnClick = async (id: ID) => {
        const guest = purchaseItem.guests.find((guest) => {
            return guest.id === id;
        });
        if (guest) {
            const guestStatus = isReturned ? GuestStatus.ARRIVED : GuestStatus.RETURNED;
            handleGuestStatusChange(guestStatus, [guest], false, ButtonSource.GUEST_RETURN_EQUIPMENT);
        }
    };

    const handleGuestNoShowClick = async (doNotIncreaseAvailability: boolean, id?: ID) => {
        if (id) {
            const guest = purchaseItem.guests.find((guest) => guest.id === id);
            if (guest) {
                const guestStatus =
                    guest.guestStatus === GuestStatus.NO_SHOW ? GuestStatus.PENDING : GuestStatus.NO_SHOW;
                handleGuestStatusChange(guestStatus, [guest], doNotIncreaseAvailability, ButtonSource.GUEST_NO_SHOW);
            }
        } else {
            const guestStatus = areAllGuestsNoShow ? GuestStatus.PENDING : GuestStatus.NO_SHOW;
            const { guests } = purchaseItem;
            handleGuestStatusChange(guestStatus, guests, doNotIncreaseAvailability, ButtonSource.GROUP_NO_SHOW);
        }
    };

    const handleNoShowModal = async (id?: ID) => {
        const guest = purchaseItem.guests.find((guest) => guest.id === id);
        // Equipment items reserve resources, not event seats, so the "increase availability" prompt does not
        // apply. Skip the modal and mark no-show directly. XS-1821.
        if (guest && guest.guestStatus === GuestStatus.NO_SHOW) {
            handleGuestNoShowClick(false, guest.id);
        } else if (isEquipmentItem(purchaseItem)) {
            guest ? handleGuestNoShowClick(false, guest.id) : handleGroupNoShowClick();
        } else if (areAllGuestsNoShow) {
            handleGroupNoShowClick();
        } else {
            showGuestNoShowModal();
            setGuestNoShowDetails({ count: guest ? 1 : totalGuestsCount, guestId: guest && id ? id : "" });
        }
    };

    const [source, setSource] = useState<ButtonSource>(ButtonSource.GROUP_NO_SHOW);

    const handleGuestStatusChange = async (
        guestStatus: GuestStatus,
        guests: Guest[],
        doNotIncreaseAvailability?: boolean,
        source?: ButtonSource,
        verificationStatus?: CheckInVerificationStatus,
    ) => {
        setSource(source ?? ButtonSource.NONE);
        await saveGuestStatus({ guestStatus, guests, verificationStatus });
        if (doNotIncreaseAvailability && event) {
            const capacity = event.max ?? event.quantity.reserved + event.open;
            const updatedCapacity = guests.length > 1 ? capacity - totalGuestsCount : capacity - 1;
            if (updatedCapacity >= 0) {
                await updateEvent({ ...event, max: updatedCapacity });
            }
        }

        invalidate("purchases", purchase.id);
        invalidate("purchaseItems", purchaseItem.id);
        if (event) {
            setTimeout(() => {
                const timeslotParams = {
                    timeslot: null,
                    event: null,
                    productId: purchaseItem.product.id,
                    startDate: purchaseItem.arrivalDate,
                };

                const updateParams = {
                    isGranted: isGranted,
                };

                updateTimeslotCache(timeslotParams, updateParams);
            }, 2000); // give some time delay for elastic search to get updated and invalidate to get new data
        }

        invalidate("events", purchaseItem.event.id);
    };

    const shouldShowSpinnerForCheckIn =
        isSaving && [ButtonSource.GUEST_CHECK_IN, ButtonSource.GROUP_CHECK_IN].includes(source);
    const shouldShowSpinnerForNoShow =
        isSaving && [ButtonSource.GUEST_NO_SHOW, ButtonSource.GROUP_NO_SHOW].includes(source);
    const shouldShowSpinnerForReturn =
        isSaving && [ButtonSource.GROUP_RETURN_EQUIPMENT, ButtonSource.GUEST_RETURN_EQUIPMENT].includes(source);

    return isCheckInEnabled ? (
        <>
            {[PurchaseItemStatus.PENDING_ACCEPT, PurchaseItemStatus.PENDING_MINIMUM].includes(
                purchaseItem.status,
            ) ? null : (
                <span className="flex">
                    {guest ? (
                        <CheckInButton
                            className="!mr-2"
                            isDisabled={isSaving || disableCheckin}
                            active={isCheckedIn}
                            isLoading={shouldShowSpinnerForCheckIn}
                            isGuestLevelButton={!!guest}
                            onClick={() => {
                                handleGuestCheckInClick(guest.id);
                            }}
                        />
                    ) : (
                        <Tooltip disabled={!isOrganizer || disableCheckin} content="Check-in all guests">
                            <CheckInButton
                                className="!mr-2"
                                isDisabled={isSaving || disableCheckin}
                                active={isCheckedIn}
                                isLoading={shouldShowSpinnerForCheckIn}
                                isGuestLevelButton={!!guest}
                                onClick={handleGroupCheckInClick}
                            />
                        </Tooltip>
                    )}

                    {showEquipmentReturn ? (
                        <Tooltip content="Return Equipment Early">
                            <CheckInButton
                                isDisabled={isSaving}
                                isLoading={shouldShowSpinnerForReturn}
                                active={isReturned}
                                action="return"
                                onClick={() => {
                                    if (guest) {
                                        handleGuestReturnClick(guest.id);
                                    } else {
                                        handleGroupReturnClick();
                                    }
                                }}
                            />
                        </Tooltip>
                    ) : guest ? (
                        <CheckInButton
                            action="noShow"
                            isDisabled={isSaving || disableCheckin}
                            active={isNoShow}
                            isLoading={shouldShowSpinnerForNoShow}
                            isGuestLevelButton={!!guest}
                            onClick={async () => handleNoShowModal(guest?.id)}
                        />
                    ) : (
                        <Tooltip disabled={!isOrganizer} content="No-show all guests">
                            <CheckInButton
                                action="noShow"
                                isDisabled={isSaving || disableCheckin}
                                active={isNoShow}
                                isLoading={shouldShowSpinnerForNoShow}
                                isGuestLevelButton={!!guest}
                                onClick={async () => handleNoShowModal()}
                            />
                        </Tooltip>
                    )}
                </span>
            )}

            <CheckInAndPrintTicket
                isOpen={isGuestForCheckInModalOpen}
                purchase={purchase}
                purchaseItem={purchaseItem}
                guests={selectedGuestsForCheckIn ?? []}
                onComplete={() => {
                    setSelectedGuestsForCheckIn(null);
                    hideGuestForCheckInModal();
                }}
            />
            <GuestNoShowModal
                guestId={noShowGuestDetails?.guestId}
                count={noShowGuestDetails.count}
                purchaseItem={purchaseItem}
                isOpen={isGuestNoShowModalOpen}
                isLoading={isUpdating}
                onConfirm={handleGuestNoShowClick}
                onClose={() => hideGuestNoShowModal()}
            />

            <MemberVerificationModal purchaseItem={purchaseItem} />
        </>
    ) : null;
};

export const getGuestsToBeCheckedIn = (guests: Guest[]) => {
    return guests.filter((guest) => {
        const alreadyCheckedInOnce = [GuestStatus.ARRIVED, GuestStatus.RETURNED].includes(guest.guestStatus);

        /** As we only allow single check-in for scheduled experiences from roster, we need to check-in only the guests with "noshow" or "pending" status. No need to check in already checked-in guests  */
        return !alreadyCheckedInOnce;
    });
};
